import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  CreditCard,
  LineChart,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Shield,
  UserCheck,
  Lock
} from 'lucide-react';
// Logo desde carpeta public
const logoSelger = '/images/logojj.jpg';

// Array de elementos del menú para facilitar mantenimiento
const menuItems = [
  { name: 'Caja Chica', icon: CreditCard, path: '/caja-chica', color: 'purple', permission: 'view_caja_chica' },
  { name: 'Reporte BI', icon: LineChart, path: '/reporte-bi', color: 'purple', permission: 'view_reportes_bi' },
];

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onMobileLinkClick?: () => void;
}

export default function Sidebar({ isCollapsed, onToggle, onMobileLinkClick }: SidebarProps) {
  const location = useLocation();
  const { user, hasPermission, logout } = useAuth();
  const [menuVisibility, setMenuVisibility] = useState<{[key: string]: boolean}>(() => {
    const saved = localStorage.getItem('menuVisibility');
    return saved ? JSON.parse(saved) : {
      'Caja Chica': true,
      'Reporte BI': true,
    };
  });

  // Filtrar elementos del menú según permisos
  const filteredMenuItems = menuItems.filter(item => hasPermission(item.permission));

  const toggleVisibility = (itemName: string) => {
    const newVisibility = {
      ...menuVisibility,
      [itemName]: !menuVisibility[itemName]
    };
    setMenuVisibility(newVisibility);
    localStorage.setItem('menuVisibility', JSON.stringify(newVisibility));
  };

  return (
    <div className={`bg-white lg:bg-gray-50 border-r border-gray-200 transition-all duration-300 ${
      isCollapsed ? 'w-16' : 'w-64'
    } h-full flex flex-col shadow-lg lg:shadow-none`}>
      
      {/* Logo y Título */}
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex items-center">
          {!isCollapsed ? (
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <img 
                  src={logoSelger} 
                  alt="J&J Operations Logo" 
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    // Fallback si la imagen no se carga
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.parentElement?.querySelector('.logo-fallback');
                    if (fallback) {
                      (fallback as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="logo-fallback w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm" style={{ display: 'none' }}>
                  <span className="text-white font-bold text-lg">SR</span>
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">J&J Operations</h1>
                <p className="text-sm text-gray-500 italic">Gestión Integral</p>
                {user && (
                  <div className="mt-2 flex items-center space-x-2">
                    {user.rol === 'Administrador' && <Shield className="w-4 h-4 text-blue-600" />}
                    {user.rol === 'Contadora' && <UserCheck className="w-4 h-4 text-green-600" />}
                    {user.rol === 'Usuario' && <Lock className="w-4 h-4 text-amber-600" />}
                    <span className="text-xs font-medium text-gray-600">{user.rol}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-shrink-0 mx-auto">
              <img 
                src={logoSelger} 
                alt="J&J Operations Logo" 
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  // Fallback si la imagen no se carga
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.parentElement?.querySelector('.logo-fallback');
                  if (fallback) {
                    (fallback as HTMLElement).style.display = 'flex';
                  }
                }}
              />
              <div className="logo-fallback w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm mx-auto" style={{ display: 'none' }}>
                <span className="text-white font-bold text-lg">SR</span>
              </div>
            </div>
          )}
        </div>
      </div>

            {/* Navegación Principal */}
            <nav className="flex-1 p-2 sm:p-4 space-y-1 sm:space-y-2 overflow-y-auto">
              {filteredMenuItems.map((item) => {
                const isActive = location.pathname === item.path;
                const IconComponent = item.icon;
                const isVisible = menuVisibility[item.name];
                
                // Definir colores según el tipo de pestaña
                const getColorClasses = (color: string, isActive: boolean) => {
                  const colorMap = {
                    blue: {
                      active: 'bg-blue-50 border-l-4 border-blue-500 text-blue-700',
                      inactive: 'text-gray-600 hover:bg-blue-50 hover:text-blue-700',
                      icon: isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600',
                      text: isActive ? 'text-blue-700' : 'text-gray-700 group-hover:text-blue-700'
                    },
                    green: {
                      active: 'bg-green-50 border-l-4 border-green-500 text-green-700',
                      inactive: 'text-green-600 hover:bg-green-50 hover:text-green-700',
                      icon: isActive ? 'text-green-600' : 'text-green-500 group-hover:text-green-600',
                      text: isActive ? 'text-green-700' : 'text-green-600 group-hover:text-green-700'
                    },
                    purple: {
                      active: 'bg-purple-50 border-l-4 border-purple-500 text-purple-700',
                      inactive: 'text-purple-600 hover:bg-purple-50 hover:text-purple-700',
                      icon: isActive ? 'text-purple-600' : 'text-purple-500 group-hover:text-purple-600',
                      text: isActive ? 'text-purple-700' : 'text-purple-600 group-hover:text-purple-700'
                    }
                  };
                  return colorMap[color as keyof typeof colorMap] || colorMap.blue;
                };
                
                const colors = getColorClasses(item.color, isActive);
                
                return (
                  <div key={item.name} className="flex items-center group">
                    <Link
                      to={item.path}
                      onClick={() => {
                        // Cerrar menú móvil al hacer clic en un enlace
                        if (onMobileLinkClick && window.innerWidth < 1024) {
                          onMobileLinkClick();
                        }
                      }}
                      className={`flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg transition-all duration-200 flex-1 text-sm sm:text-base ${
                        isActive 
                          ? colors.active
                          : colors.inactive
                      } ${!isVisible ? 'opacity-50' : ''}`}
                    >
                      <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${colors.icon}`} />
                      {!isCollapsed && (
                        <span className={`font-medium ${colors.text} truncate`}>
                          {item.name}
                        </span>
                      )}
                    </Link>
                    
                    {/* Botón de visibilidad */}
                    {!isCollapsed && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleVisibility(item.name);
                        }}
                        className={`ml-2 p-1 rounded hover:bg-gray-200 transition-colors ${
                          isVisible ? 'text-gray-600' : 'text-gray-400'
                        }`}
                        title={isVisible ? 'Ocultar pestaña' : 'Mostrar pestaña'}
                      >
                        {isVisible ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </nav>

      {/* Cerrar Sesión */}
      <div className="p-2 sm:p-4 border-t border-gray-200">
        <button 
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
              console.log('🔄 [Sidebar] Iniciando cierre de sesión...');
              await logout();
              console.log('✅ [Sidebar] Sesión cerrada, redirigiendo...');
              // Usar replace en lugar de href para evitar que se pueda volver atrás
              window.location.replace('/');
            } catch (error) {
              console.error('❌ [Sidebar] Error al cerrar sesión:', error);
              // Intentar redirigir de todas formas
              window.location.replace('/');
            }
          }}
          className={`w-full flex items-center space-x-2 sm:space-x-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg transition-all duration-200 group text-gray-600 hover:bg-red-50 hover:text-red-700 text-sm sm:text-base ${
            isCollapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 text-gray-500 group-hover:text-red-600" />
          {!isCollapsed && (
            <span className="font-medium group-hover:text-red-700">Cerrar Sesión</span>
          )}
        </button>
      </div>


      {/* Toggle Button - Oculto en móvil */}
      <div className="hidden lg:block p-2 sm:p-4 border-t border-gray-200">
        <button
          onClick={onToggle}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200`}
          title={isCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <>
              <span className="text-sm font-medium">Colapsar</span>
              <ChevronLeft className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}